import { ChangeDetectionStrategy, Component, ElementRef, Injector, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonRow,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { PlatformService } from '@okcargo-apps/shared-infrastructure-ionic';
import { DecaMutationsService } from '../deca-mutations.service';
import { DecaStoreService } from '../deca-store.service';

@Component({
  selector: 'lib-deca-promo-carriers-request-modal',
  templateUrl: './deca-promo-carriers-request-modal.component.html',
  styleUrl: './deca-promo-carriers-request-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonIcon,
    IonTitle,
    IonContent,
    IonGrid,
    IonRow,
    IonCol,
    IonItem,
    IonCheckbox,
    IonFooter,
    IonButton,
    TranslateModule,
  ],
})
export class DecaPromoCarriersRequestModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly decaMutationsService = inject(DecaMutationsService);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly platformService = inject(PlatformService);

  public readonly isHybrid = this.platformService.isHybrid;

  public readonly form = this.fb.group({
    acceptTerms: this.fb.nonNullable.control(false, [Validators.requiredTrue]),
  });

  public readonly submitError = signal<string | null>(null);

  protected get hasTermsError(): boolean {
    const control = this.form.controls.acceptTerms;
    return control.invalid && control.touched;
  }

  public async submit(): Promise<boolean> {
    if (this.form.invalid) {
      markFormAsInvalid(this.form, this.elementRef.nativeElement, this.injector);
      return false;
    }

    const loading = await this.loadingCtrl.create({
      message: 'Enviando datos...',
    });
    await loading.present();

    try {
      await this.decaMutationsService.requestPromotionalCredits();

      this.messagesUiService.setMessage({
        objectId: 'dashboard',
        message: ['Ha enviado una solicitud de créditos promocionales para generar DeCAs'],
        durationMs: 3000,
      });

      this.decaStoreService.reloadAvailableCreditsAfterSave();

      return this.modalCtrl.dismiss(true, 'confirm');
    } catch (error) {
      // TODO manage error
      console.error('Error requesting promotional credits', error);
      this.submitError.set('No se ha podido enviar la solicitud. Inténtalo de nuevo.');
      return false;
    } finally {
      await loading.dismiss();
    }
  }

  public closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
